import React, { useEffect, useRef, useState } from 'react';
import { Drawer, Spin, Radio, Input, Button, Tooltip, message } from 'antd';
import { connect } from 'react-redux';
import intl from 'react-intl-universal';

import * as TransferApi from '@/api/bill/transfer';
import {
  toggleTransferModalVisible,
  setSelectedTargetRoom,
  setSelectedTargetRoomId,
  resetTargets,
} from '@/redux/actions/bill/transfer';

import { DrawerHeader, DrawerFooter } from '@/components/PortalDrawer';
import ThrottleButton from '@/components/Reservation/ThrottleButton';

import BillPicker, { BillRoomType } from './BillPicker';

import styles from './index.module.scss';

enum TransferType {
  OrderNo,
  RoomState,
}

function Transfer(props) {
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [modalData, setModalData] = useState<{
    transferType: TransferType; // 转账方式
    roomList: BillRoomType[]; // 房型数据
    targetOrderNo: string; // 按房间订单号 【主单号】
    targetRecNo: string; // 按房态【子单号】
  }>({
    transferType: TransferType.RoomState,
    roomList: [],
    targetOrderNo: null,
    targetRecNo: null,
  });
  const updateFlagRef = useRef(false);

  const { modalVisible, selectedBillIds } = props;
  const billList = selectedBillIds || [];
  const { transferType, targetOrderNo, roomList, targetRecNo } = modalData;

  // 关闭
  const closeModal = () => {
    props.toggleTransferModalVisible(false);
    if (updateFlagRef.current) {
      props.reloadList();
    }
  };

  // 修改类型
  const crateTypeHandler = (type) => (event) => {
    if (event.target.checked) {
      setModalData({ ...modalData, transferType: type });
    }
  };

  // 更新
  const updateModalData = (data: Partial<typeof modalData>) => {
    setModalData({ ...modalData, ...data });
  };

  // 订单号转账
  const handleOrderNoChange = (event) => {
    updateModalData({ targetOrderNo: event.target.value });
  };

  // 房间号转账
  const handleRecNoChange = (val) => {
    updateModalData({ targetRecNo: val });
  };

  // 初始化
  const initBaseData = async () => {
    setLoadingStatus(true);
    setModalData({
      transferType: TransferType.RoomState,
      roomList: [],
      targetOrderNo: null,
      targetRecNo: null,
    });
    updateFlagRef.current = false;
    try {
      const res = await TransferApi.fetchTransferTargets();
      setModalData({
        transferType: TransferType.RoomState,
        roomList: res.data || [],
        targetOrderNo: null,
        targetRecNo: null,
      });
    } catch (_) {
      //
    }
    setLoadingStatus(false);
  };

  // 校验、处理入参
  const validateAndFormatData = () => {
    const params = {
      sourceResvNo: props.resvNo, // 原单主单
      billItemNoList: billList, // 选中账单列表
      srcBillNo: props.orderBillNo, // 源账单号
      resvNo: undefined, // 按订单号转账
      recNo: undefined, // 按房态转
    };

    // 按订单号转入
    if (transferType === TransferType.OrderNo) {
      params.resvNo = targetOrderNo;
      if (!targetOrderNo) {
        message.warning(intl.get('bill_transfer_order_no_null'));
        return null;
      }
    }

    // 按房间转入
    if (transferType === TransferType.RoomState) {
      if (targetRecNo) {
        // 二次校验 有脏东西
        roomList.some((item) => {
          return (item.roomList || []).some((room) => {
            if (room.recNo === targetRecNo) {
              params.recNo = targetRecNo;
              return true;
            }
            return false;
          });
        });
      }
      if (!params.recNo) {
        message.error(intl.get('bill_transfer_select_room'));
        return null;
      }
    }

    return params;
  };

  // 提交
  const handleSubmitClick = async () => {
    setLoadingStatus(true);
    try {
      const params = validateAndFormatData();
      if (params) {
        await TransferApi.confirmTransfer(params);
        message.success(intl.get('bill_transfer_success'));
        updateFlagRef.current = true;
        closeModal();
      }
    } catch (_) {
      //
    }
    setLoadingStatus(false);
  };

  // 提交按钮
  const renderSubmitButton = () => {
    // 按房态 未选择
    if (transferType === TransferType.RoomState && !targetRecNo) {
      return (
        <Tooltip title={intl.get('bill_transfer_select_room')}>
          <Button style={{ width: 100 }} disabled>
            {intl.get('bill_transfer_confirm')}
          </Button>
        </Tooltip>
      );
    }

    return (
      <ThrottleButton type="primary" throttleTime={1000} onClick={handleSubmitClick}>
        {intl.get('bill_transfer_confirm')}
      </ThrottleButton>
    );
  };

  useEffect(() => {
    if (modalVisible) {
      initBaseData();
    }
  }, [modalVisible]);

  return (
    <Drawer closable={false} destroyOnClose className={styles.transferDrawer} open={modalVisible}>
      <Spin spinning={loadingStatus}>
        <DrawerHeader title={intl.get('bill_transfer')} onClose={closeModal} />
        <div className={styles.contentWrapper}>
          <div>{intl.get('bill_transfer_checked_bill_count', { count: billList.length })}</div>
          <div className={styles.typeRadioWrapper}>
            <Radio
              checked={transferType === TransferType.OrderNo}
              onChange={crateTypeHandler(TransferType.OrderNo)}
            >
              {intl.get('bill_transfer_type_order_no')}
            </Radio>
          </div>
          <div>
            <Input
              placeholder={intl.get('bill_transfer_order_no_hint')}
              maxLength={100}
              value={targetOrderNo}
              onChange={handleOrderNoChange}
            />
          </div>
          <div className={styles.typeRadioWrapper}>
            <Radio
              checked={transferType === TransferType.RoomState}
              onChange={crateTypeHandler(TransferType.RoomState)}
            >
              {intl.get('bill_transfer_type_room_state')}
            </Radio>
          </div>
          <BillPicker dataSource={roomList} value={targetRecNo} onChange={handleRecNoChange} />
        </div>
        <DrawerFooter>
          {renderSubmitButton()}
          <Button type="default" onClick={closeModal}>
            {intl.get('app_confirm_cancel_btn')}
          </Button>
        </DrawerFooter>
      </Spin>
    </Drawer>
  );
}

const mapStateToProps = (state) => {
  const { orderBillNo, selectedBillIds } = state.bill;
  const { modalVisible, roomsLoading, rooms, roomTypes, selectedRoom } = state.transfer;
  return {
    orderBillNo,
    selectedBillIds,
    modalVisible,
    roomsLoading,
    rooms,
    roomTypes,
    selectedRoom,
  };
};

export default connect(mapStateToProps, {
  toggleTransferModalVisible,
  setSelectedTargetRoom,
  setSelectedTargetRoomId,
  resetTargets,
})(Transfer);
